import { Api } from '@monorepo/shared/apiClient';
import { LogEntry } from '@monorepo/shared/componentsV2/logs/types';
import { FileData } from '@monorepo/shared/hooks/useFileInputHandler';
import { EdpAiFileWithUrl } from 'mapistry-shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

export enum FileUploadStatus {
  Processing = 'processing',
  Ready = 'ready',
  Completed = 'completed',
  Error = 'error',
}

export interface UploadedFile {
  type: 'UploadedFile';
  originFile: File;
  name: string;
  mimeType: string;
  fileBlobUrl: string;
  logEntries: LogEntry[];
  status: FileUploadStatus;
  requestId: string;
  error?: Api.ErrorResponse;
  orderNumber?: number | null;
  processedFile?: EdpAiFileWithUrl;
}

export interface ExistingFile {
  type: 'ExistingFile';
  logEntries: LogEntry[];
  mimeType?: string;
  fileBlobUrl?: string;
}

export type SelectedFile = UploadedFile | ExistingFile;

export function useUploadedFiles(fileData: FileData[]) {
  const [uploadedFiles, setUploadedFiles] = useState<Map<string, UploadedFile>>(
    new Map(),
  );

  useEffect(() => {
    setUploadedFiles((prev) => {
      const next = new Map(prev);
      const fileDataRequestIds = new Set(fileData.map((fd) => fd.requestId));

      // Remove entries for files no longer in fileData
      let didRemove = false;
      Array.from(next.keys()).forEach((requestId) => {
        if (!fileDataRequestIds.has(requestId)) {
          next.delete(requestId);
          didRemove = true;
        }
      });

      // Reassign remaining orderNumbers so "File X of N" stays in bounds
      if (didRemove) {
        const ordered = Array.from(next.entries())
          .filter(([, f]) => f.orderNumber != null)
          .sort(
            ([, a], [, b]) =>
              (a.orderNumber ?? Infinity) - (b.orderNumber ?? Infinity),
          );
        ordered.forEach(([id, file], idx) => {
          next.set(id, { ...file, orderNumber: idx + 1 });
        });
      }

      // Add or preserve entries for current fileData
      fileData.forEach((fd) => {
        const existing = next.get(fd.requestId);
        if (!existing) {
          next.set(fd.requestId, {
            originFile: fd.file,
            name: fd.file.name,
            mimeType: fd.file.type,
            fileBlobUrl: fd.fileBlobUrl,
            processedFile: undefined,
            logEntries: [],
            status: FileUploadStatus.Processing,
            error: undefined,
            requestId: fd.requestId,
            orderNumber: null,
            type: 'UploadedFile',
          });
        }
      });
      return next;
    });
  }, [fileData]);

  const statusCounts = useMemo(
    () =>
      Array.from(uploadedFiles.values()).reduce(
        (counts, file) => {
          if (file.status === FileUploadStatus.Processing)
            return { ...counts, processing: counts.processing + 1 };
          if (file.status === FileUploadStatus.Ready)
            return { ...counts, ready: counts.ready + 1 };
          if (file.status === FileUploadStatus.Completed)
            return { ...counts, completed: counts.completed + 1 };
          if (file.status === FileUploadStatus.Error)
            return { ...counts, error: counts.error + 1 };
          return counts;
        },
        { processing: 0, ready: 0, completed: 0, error: 0 },
      ),
    [uploadedFiles],
  );

  const isProcessingSomeFiles = statusCounts.processing > 0;
  const hasAnyErrorFiles = statusCounts.error > 0;
  const areAllFilesCompletedOrHaveErrors =
    uploadedFiles.size > 0 &&
    statusCounts.completed + statusCounts.error === uploadedFiles.size;
  const areAnyFilesProcessingAndNotReady =
    uploadedFiles.size > 0 &&
    statusCounts.processing > 0 &&
    statusCounts.ready === 0;
  const areMultipleReadyFilesOrProcessing =
    statusCounts.ready > 1 || statusCounts.processing > 0;

  const fileCompleted = useCallback((requestId: string) => {
    setUploadedFiles((prev) => {
      const next = new Map(prev);
      const existing = prev.get(requestId);
      if (!existing) return prev;
      next.set(requestId, {
        ...existing,
        status: FileUploadStatus.Completed,
      });
      return next;
    });
  }, []);

  const updateLogEntriesForFile = useCallback(
    (requestId: string, logEntries: LogEntry[] | undefined) => {
      setUploadedFiles((prev) => {
        const next = new Map(prev);
        const existing = prev.get(requestId);
        if (!existing) return prev;
        next.set(requestId, {
          ...existing,
          logEntries: logEntries ?? [],
        });
        return next;
      });
    },
    [],
  );

  const fileIsReady = useCallback(
    (
      requestId: string,
      logEntries: LogEntry[] | undefined,
      processedFile: EdpAiFileWithUrl | undefined,
    ) => {
      setUploadedFiles((prev) => {
        const next = new Map(prev);
        const existing = prev.get(requestId);
        if (!existing) return prev;
        const maxOrder = Math.max(
          0,
          ...Array.from(prev.values()).map((f) => f.orderNumber ?? 0),
        );
        const orderNumber = maxOrder ? maxOrder + 1 : 1;
        next.set(requestId, {
          ...existing,
          processedFile,
          logEntries: logEntries ?? [],
          status: FileUploadStatus.Ready,
          orderNumber,
        });
        return next;
      });
    },
    [],
  );

  const fileErrored = useCallback(
    (
      requestId: string,
      error?: Api.ErrorResponse,
      processedFile?: EdpAiFileWithUrl,
    ) => {
      setUploadedFiles((prev) => {
        const next = new Map(prev);
        const existing = prev.get(requestId);
        if (!existing) return prev;
        next.set(requestId, {
          ...existing,
          error: error ?? undefined,
          status: FileUploadStatus.Error,
          processedFile,
        });
        return next;
      });
    },
    [],
  );

  return {
    uploadedFiles,
    isProcessingSomeFiles,
    areAllFilesCompletedOrHaveErrors,
    areAnyFilesProcessingAndNotReady,
    hasAnyErrorFiles,
    areMultipleReadyFilesOrProcessing,
    statusCounts,
    fileCompleted,
    updateLogEntriesForFile,
    fileIsReady,
    fileErrored,
  };
}
